import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import Navbar from './components/Navbar.tsx';
import BottomTabBar from './components/BottomTabBar.tsx';
import MobileHeader from './components/MobileHeader.tsx';
import Dashboard from './pages/Dashboard.tsx';
import Schedule from './pages/Schedule.tsx';
import CheckIn from './pages/CheckIn.tsx';
import History from './pages/History.tsx';
import Profile from './pages/Profile.tsx';
import Leave from './pages/Leave.tsx';
import Payslips from './pages/Payslips.tsx';
import Tasks from './pages/Tasks.tsx';
import TransactionRequest from './pages/TransactionRequest.tsx';
import Claims from './pages/Claims.tsx';
import PaymentVerification from './pages/PaymentVerification.tsx';
import ScheduleDetail from './pages/ScheduleDetail.tsx';
import ScheduleHistory from './pages/ScheduleHistory.tsx';
import Login from './pages/Login.tsx';
import PestControlForm from './pages/PestControlForm.tsx';
import PesticideApplicationRecord from './pages/PesticideApplicationRecord.tsx';
import PendingSignaturesPage from './pages/PendingSignaturesPage.tsx';
import ManagerHistory from './pages/ManagerHistory.tsx';
import ManagerManagement from './pages/ManagerManagement.tsx';
import SplashScreen from './components/SplashScreen.tsx';
import { AuthProvider } from './context/AuthContext.tsx';
import { PopupProvider } from './context/PopupContext.tsx';
import { ConfirmProvider } from './context/ConfirmContext.tsx';
import { NotificationProvider } from './context/NotificationContext.tsx';
import ProtectedRoute from './components/ProtectedRoute.tsx';
import { initializeFirebaseMessaging } from './services/firebase.ts';

// Wrapper component to get the title for Schedule Detail
const ScheduleDetailWrapper = () => {
  return (
    <>
      <Navbar />
      <MobileHeader showBackButton={true} />
      <main className="flex-grow pb-16 md:pb-0">
        <ScheduleDetail />
      </main>
      <BottomTabBar />
    </>
  );
};

// Wrapper for Schedule History
const ScheduleHistoryWrapper = () => {
  return (
    <>
      <Navbar />
      <MobileHeader showBackButton={true} title="Schedule History" />
      <main className="flex-grow pb-16 md:pb-0">
        <ScheduleHistory />
      </main>
      <BottomTabBar />
    </>
  );
};

// Wrapper for CheckIn
const CheckInWrapper = () => {
  return (
    <>
      <Navbar />
      <MobileHeader showBackButton={true} title="Check In" />
      <main className="flex-grow pb-16 md:pb-0">
        <CheckIn />
      </main>
      <BottomTabBar />
    </>
  );
};
// Wrapper for Leave
const LeaveWrapper = () => {
  return (
    <>
      <Navbar />
      <MobileHeader showBackButton={true} title="Leave" />
      <main className="flex-grow pb-16 md:pb-0">
        <Leave />
      </main>
      <BottomTabBar />
    </>
  );
};

// Wrapper for Transaction Request
const TransactionRequestWrapper = () => {
  return (
    <>
      <Navbar />
      <MobileHeader showBackButton={true} title="Transaction" />
      <main className="flex-grow pb-16 md:pb-0">
        <TransactionRequest />
      </main>
      <BottomTabBar />
    </>
  );
};

// Wrapper for Claims
const ClaimsWrapper = () => {
  return (
    <>
      <Navbar />
      <MobileHeader showBackButton={true} title="Claims" />
      <main className="flex-grow pb-16 md:pb-0">
        <Claims />
      </main>
      <BottomTabBar />
    </>
  );
};

// Wrapper for Payment Verification
const PaymentVerificationWrapper = () => {
  return (
    <>
      <Navbar />
      <MobileHeader showBackButton={true} title="Payment Verification" />
      <main className="flex-grow pb-16 md:pb-0">
        <PaymentVerification />
      </main>
      <BottomTabBar />
    </>
  );
};


// Wrapper for PestControlForm
const PestControlWrapper = () => {
  return (
    <>
      <Navbar />
      <MobileHeader showBackButton={true} title="Pest Control Findings" />
      <main className="flex-grow pb-16 md:pb-0">
        <PestControlForm />
      </main>
      <BottomTabBar />
    </>
  );
};

// Wrapper for PesticideApplicationRecord
const PesticideApplicationWrapper = () => {
  return (
    <>
      <Navbar />
      <MobileHeader showBackButton={true} title="Pesticide Application Record" />
      <main className="flex-grow pb-16 md:pb-0">
        <PesticideApplicationRecord />
      </main>
      <BottomTabBar />
    </>
  );
};

// Wrapper for PendingSignaturesPage
const PendingSignaturesWrapper = () => {
  return (
    <>
      <Navbar />
      <MobileHeader showBackButton={true} title="Pending Signatures" />
      <main className="flex-grow pb-16 md:pb-0">
        <PendingSignaturesPage />
      </main>
      <BottomTabBar />
    </>
  );
};

// Wrapper for ManagerManagement
const ManagerManagementWrapper = () => {
  return (
    <>
      <Navbar />
      <MobileHeader showBackButton={true} title="Manager Management" />
      <main className="flex-grow pb-16 md:pb-0">
        <ManagerManagement />
      </main>
      <BottomTabBar />
    </>
  );
};

// Wrapper for ManagerHistory
const ManagerHistoryWrapper = () => {
  return (
    <>
      <Navbar />
      <MobileHeader showBackButton={true} title="Manager History" />
      <main className="flex-grow pb-16 md:pb-0">
        <ManagerHistory />
      </main>
      <BottomTabBar />
    </>
  );
};

function App() {
  const [appReady, setAppReady] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  
  // Handle splash screen completion
  const handleSplashFinish = () => {
    setShowSplash(false);
  };
  
  // Mark app as ready once components have loaded
  useEffect(() => {
    // Simulate app loading process
    const appLoadTimeout = setTimeout(() => {
      setAppReady(true);
    }, 500);
    
    return () => clearTimeout(appLoadTimeout);
  }, []);

  // Trigger splash screen finish when app is ready
  useEffect(() => {
    if (appReady && showSplash) {
      // Allow some minimum time for splash screen to be visible
      // The SplashScreen component already ensures a minimum display time
    }
  }, [appReady, showSplash]);



  return (
    <AuthProvider>
      <PopupProvider>
        <ConfirmProvider>
          <NotificationProvider>
            {showSplash && <SplashScreen onFinish={handleSplashFinish} />}
            
            <Router>
              <div className={`flex flex-col min-h-screen transition-opacity duration-300 ${showSplash ? 'opacity-0' : 'opacity-100'}`}>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/" element={
                    <ProtectedRoute>
                      <Navbar />
                      <MobileHeader />
                      <main className="flex-grow pb-16 md:pb-0">
                        <Dashboard />
                      </main>
                      <BottomTabBar />
                    </ProtectedRoute>
                  } />
                  <Route path="/schedule" element={
                    <ProtectedRoute>
                      <Navbar />
                      <MobileHeader />
                      <main className="flex-grow pb-16 md:pb-0">
                        <Schedule />
                      </main>
                      <BottomTabBar />
                    </ProtectedRoute>
                  } />
                  <Route path="/schedule/:id" element={
                    <ProtectedRoute>
                      <ScheduleDetailWrapper />
                    </ProtectedRoute>
                  } />
                  <Route path="/checkin" element={
                    <ProtectedRoute>
                      <CheckInWrapper />
                    </ProtectedRoute>
                  } />
                  <Route path="/history" element={
                    <ProtectedRoute>
                      <Navbar />
                      <MobileHeader />
                      <main className="flex-grow pb-16 md:pb-0">
                        <History />
                      </main>
                      <BottomTabBar />
                    </ProtectedRoute>
                  } />
                  <Route path="/leave" element={
                    <ProtectedRoute>
                      <LeaveWrapper />
                    </ProtectedRoute>
                  } />
                  <Route path="/transaction-request" element={
                    <ProtectedRoute>
                      <TransactionRequestWrapper />
                    </ProtectedRoute>
                  } />
                  <Route path="/claims" element={
                    <ProtectedRoute>
                      <ClaimsWrapper />
                    </ProtectedRoute>
                  } />
                  <Route path="/payment-verification" element={
                    <ProtectedRoute>
                      <PaymentVerificationWrapper />
                    </ProtectedRoute>
                  } />
                  <Route path="/payslips" element={
                    <ProtectedRoute>
                      <Navbar />
                      <MobileHeader showBackButton={true} title="Documents" />
                      <main className="flex-grow pb-16 md:pb-0">
                        <Payslips />
                      </main>
                      <BottomTabBar />
                    </ProtectedRoute>
                  } />
                  <Route path="/tasks" element={
                    <ProtectedRoute>
                      <Navbar />
                      <MobileHeader showBackButton={true} title="Tasks" />
                      <main className="flex-grow pb-16 md:pb-0">
                        <Tasks />
                      </main>
                      <BottomTabBar />
                    </ProtectedRoute>
                  } />
                  <Route path="/profile" element={
                    <ProtectedRoute>
                      <Navbar />
                      <MobileHeader />
                      <main className="flex-grow pb-16 md:pb-0">
                        <Profile />
                      </main>
                      <BottomTabBar />
                    </ProtectedRoute>
                  } />
                  <Route path="/pest-control" element={
                    <ProtectedRoute>
                      <PestControlWrapper />
                    </ProtectedRoute>
                  } />
                  <Route path="/pesticide-application" element={
                    <ProtectedRoute>
                      <PesticideApplicationWrapper />
                    </ProtectedRoute>
                  } />
                  <Route path="/pending-signatures" element={
                    <ProtectedRoute>
                      <PendingSignaturesWrapper />
                    </ProtectedRoute>
                  } />
                  <Route path="/manager-management" element={
                    <ProtectedRoute>
                      <ManagerManagementWrapper />
                    </ProtectedRoute>
                  } />
                  <Route path="/manager-history" element={
                    <ProtectedRoute>
                      <ManagerHistoryWrapper />
                    </ProtectedRoute>
                  } />
                  <Route path="/schedule-history/:id" element={
                    <ProtectedRoute>
                      <ScheduleHistoryWrapper />
                    </ProtectedRoute>
                  } />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </div>
            </Router>
          </NotificationProvider>
        </ConfirmProvider>
      </PopupProvider>
    </AuthProvider>
  );
}

export default App; 